import {
    addMinutes,
    format,
    fromUnixTime,
    isWithinInterval as isWithinIntervalDateFns, max, min,
    roundToNearestMinutes
} from "date-fns";
import { dateFromState, dateToState } from "./DateTimeHelper";

export const intervalToState = (objectInterval) => {
    if (objectInterval === null) {
        return null;
    }

    return {
        ...objectInterval,
        start: dateToState(objectInterval.start),
        end: dateToState(objectInterval.end)
    }
}

export const intervalFromState = (stringInterval) => {
    if (!stringInterval) {
        return null;
    }

    if (stringInterval.start instanceof Date) {
        return {...stringInterval};
    }

    return {
        ...stringInterval,
        start: dateFromState(stringInterval.start),
        end: dateFromState(stringInterval.end),
    }
}

export const intervalArrayFromState = (intervalArray) => {
    if (!Array.isArray(intervalArray)) {
        return [];
    }
    return intervalArray.map((interval) => intervalFromState(interval))
}

export const intervalArrayToState = (intervalArray) => {
    if (!Array.isArray(intervalArray)) {
        return [];
    }
    return intervalArray.map((interval) => intervalToState(interval))
}

/**
 *
 * @param {object} interval
 * @returns {*[]}
 */
export const intervalToTimestampArray = (interval) => {
    if (!interval || !interval.start) {
        return null;
    }
    interval = intervalFromState(interval);
    return [
        interval.start.getTime(),
        interval.end.getTime(),
    ];
}

/**
 * Snaps the start/end time of the given interval to the previous/next
 * interval in the given array of intervals.
 *
 * Assumes that intervals arrays is correctly sorted by start time
 * @param {{start: *, end: *}} interval
 * @param {array} intervals
 * @returns {{start: *, end: *}|*}
 */

export function snapInterval(interval, intervals) {
    if (!intervals || !intervals.length) {
        return interval;
    }

    interval = intervalFromState(interval);
    intervals = intervalArrayFromState(intervals);

    const { start, end } = interval;

    // Find the latest start time earlier than or equal to the current start time
    let latestStart = intervals[0].start;
    for (let i = 1; i < intervals.length; i++) {
        const currentStart = intervals[i].start;
        if (currentStart <= start) {
            latestStart = currentStart;
        } else {
            break;
        }
    }

    // Find the earliest end time later than or equal to the current end time
    let earliestEnd = intervals[intervals.length - 1].end;
    for (let i = intervals.length - 2; i >= 0; i--) {
        const currentEnd = intervals[i].end;
        if (currentEnd >= end) {
            earliestEnd = currentEnd;
        } else {
            break;
        }
    }

    return { start: latestStart, end: earliestEnd };
}

/**
 * Wraps the date-fns function to ensure date strings are converted to dates
 * @param date
 * @param interval
 * @returns {*}
 */
export const isWithinInterval = (date, interval) => {
    if (!interval) {
        return false;
    }
    return isWithinIntervalDateFns(dateFromState(date), intervalFromState(interval));
}

export const intervalsOverlap = (interval1, interval2) => {
    interval1 = intervalFromState(interval1);
    interval2 = intervalFromState(interval2);

    return (interval1.start < interval2.end
        && interval1.end > interval2.start
    );
}
/**
 * @TODO - Was written to compare timestamps
 * @param start
 * @param startInterval
 * @param endInterval
 * @returns {boolean}
 */
const isStartInBlockedInterval = (start, startInterval, endInterval) => {
    return (start >= startInterval && start < endInterval);
}

/**
 * @TODO was written to compare timestamps
 *
 * @param end
 * @param startInterval
 * @param endInterval
 * @returns {boolean}
 */
const isEndInBlockedInterval = (end, startInterval, endInterval) => {
    return (end <= endInterval && end > startInterval);
}

/**
 * Returns interval duration in ms
 * @param interval
 * @returns {number}
 */
export const intervalDuration = (interval) => {
    if (!interval || !interval.start) {
        return 0;
    }
    const timestamps = intervalToTimestampArray(interval);
    return timestamps[1] - timestamps[0];
}

export const intervalDurationInMinutes = (interval) => {
    return intervalDuration(interval) / 1000 / 60;
}

/**
 * Convert a date interval with JS dates to unix
 * @param {object} interval
 * @returns {*}
 */
export const unixIntervalToDate = (interval) => {
    return {
        start: dateToState(fromUnixTime(interval.start)),
        end: dateToState(fromUnixTime(interval.end)),
    };
}

/**
 * Convert a date interval with Unix time stamps to js dates
 * @param {object} interval
 * @returns {{start: number, end: number}}
 */
export const dateIntervalToUnix = (interval) => {
    interval = intervalFromState(interval);
    return {
        start: Number(format(interval.start, 't')),
        end: Number(format(interval.end, 't')),
    };
}

/**
 *
 * @param {object} interval
 * @param {string} date
 * @returns {{start: Date, end: Date}}
 */
export const setIntervalDatesButKeepTimes = (interval, date) => {
    // TODO: Needs to just set the start date and maintain the duration, for intervals that cross date boundaries
    let dateObject = dateFromState(date);
    interval = intervalFromState(interval);

    let newInterval = {
        start: new Date(dateObject.valueOf()),
        end: new Date(dateObject.valueOf()),
    };

    newInterval.start.setHours(interval.start.getHours(), interval.start.getMinutes(), 0);
    newInterval.end.setHours(interval.end.getHours(), interval.end.getMinutes(), 0);

    return newInterval;
}

export const getTimelineDivisions = (interval, minutesPerDivision = 15) => {
    interval = intervalFromState(interval);

    let output = [];
    let tickTime = interval.start;

    while (tickTime <= interval.end) {
        output.push(tickTime.getTime())
        tickTime = addMinutes(tickTime, minutesPerDivision);
    }

    return output;
}

export const roundInterval = (interval, minutes) => {
    return {
        start: roundToNearestMinutes(interval.start, {nearestTo: minutes}),
        end: roundToNearestMinutes(interval.end, {nearestTo: minutes})
    }
}

export const clampInterval = (innerInterval, boundaryInterval) => {
    return {
        ...innerInterval,
        start: max([innerInterval.start, boundaryInterval.start]),
        end: min([innerInterval.end, boundaryInterval.end])
    }
}
